import { createTheme } from '@mui/material/styles';

// Unified compact UI: keep MUI defaults `size: 'small'` everywhere,
// plus tighter paddings / font sizes so inputs, selects, buttons,
// chips and tables share one consistent dense look across pages.

const theme = createTheme({
    palette: {
        mode: 'dark',
    },
    typography: {
        fontSize: 12,
        button: {
            textTransform: 'none',
            fontSize: '0.75rem',
        },
        caption: {
            fontSize: '0.7rem',
        },
    },
    shape: {
        borderRadius: 4,
    },
    components: {
        // ---- Buttons ----
        MuiButton: {
            defaultProps: { size: 'small', disableElevation: true },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    minHeight: 24,
                    paddingTop: 2,
                    paddingBottom: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                },
                sizeSmall: {
                    minHeight: 24,
                    paddingTop: 2,
                    paddingBottom: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                },
            },
        },
        MuiButtonGroup: {
            defaultProps: { size: 'small' },
        },
        MuiIconButton: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: { padding: 4 },
                sizeSmall: { padding: 4 },
            },
        },
        MuiToggleButton: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                },
            },
        },
        MuiToggleButtonGroup: {
            defaultProps: { size: 'small' },
        },
        MuiFab: {
            defaultProps: { size: 'small' },
        },

        // ---- Inputs / Selects ----
        MuiTextField: {
            defaultProps: { size: 'small', variant: 'outlined' },
        },
        MuiFormControl: {
            defaultProps: { size: 'small', variant: 'outlined' },
        },
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
                input: { fontSize: '0.78rem' },
                inputSizeSmall: {
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
                input: {
                    paddingTop: 4,
                    paddingBottom: 4,
                },
                inputSizeSmall: {
                    paddingTop: 4,
                    paddingBottom: 4,
                    paddingLeft: 8,
                    paddingRight: 8,
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                input: { paddingTop: 6, paddingBottom: 4 },
            },
        },
        MuiInput: {
            styleOverrides: {
                input: { paddingTop: 2, paddingBottom: 2, fontSize: '0.78rem' },
            },
        },
        MuiSelect: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                select: { fontSize: '0.78rem' },
                outlined: {
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                inputRoot: {
                    paddingTop: 2,
                    paddingBottom: 2,
                },
                option: {
                    fontSize: '0.78rem',
                    minHeight: 28,
                },
            },
        },
        MuiInputLabel: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                root: { fontSize: '0.78rem' },
                sizeSmall: { fontSize: '0.78rem' },
                outlined: {
                    // Re-center the resting label for our compact OutlinedInput
                    // (default sizeSmall is translate(14px, 9px) — too low here).
                    transform: 'translate(10px, 5px) scale(1)',
                    '&.MuiInputLabel-sizeSmall': {
                        transform: 'translate(10px, 5px) scale(1)',
                    },
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(12px, -7px) scale(0.78)',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: { fontSize: '0.68rem', marginTop: 2 },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
            },
        },

        // ---- Toggles ----
        MuiCheckbox: {
            defaultProps: { size: 'small' },
            styleOverrides: { root: { padding: 4 } },
        },
        MuiRadio: {
            defaultProps: { size: 'small' },
            styleOverrides: { root: { padding: 4 } },
        },
        MuiSwitch: {
            defaultProps: { size: 'small' },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: { fontSize: '0.78rem' },
            },
        },

        // ---- Menus / Lists ----
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.78rem',
                    minHeight: 28,
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: { paddingTop: 4, paddingBottom: 4 },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: { fontSize: '0.82rem' },
                secondary: { fontSize: '0.7rem' },
            },
        },

        // ---- Chips ----
        MuiChip: {
            defaultProps: { size: 'small' },
            styleOverrides: {
                sizeSmall: { height: 20, fontSize: '0.68rem' },
                labelSmall: { paddingLeft: 6, paddingRight: 6 },
            },
        },

        // ---- Tables ----
        MuiTable: {
            defaultProps: { size: 'small' },
        },
        MuiTableCell: {
            styleOverrides: {
                root: { fontSize: '0.78rem' },
                sizeSmall: { padding: '4px 8px' },
                head: { fontWeight: 600 },
            },
        },

        // ---- Tabs ----
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    minHeight: 32,
                    fontSize: '0.78rem',
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { minHeight: 32 },
            },
        },

        // ---- Tooltips / dialogs ----
        MuiTooltip: {
            styleOverrides: {
                tooltip: { fontSize: '0.7rem' },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: { fontSize: '1rem', paddingTop: 10, paddingBottom: 6 },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: { fontSize: '0.82rem' },
            },
        },
    },
});

export default theme;